import type { CSSProperties } from "react";

/**
 * Skeleton — placeholder de chargement (anim Tailwind `animate-pulse`).
 *
 * Variantes :
 *  - `text`   : N lignes grises avec largeurs variées (full, 5/6, 2/3)
 *  - `rect`   : bloc rectangulaire (width/height au choix)
 *  - `circle` : disque (taille unique en px)
 */
export type SkeletonProps =
  | {
      variant: "text";
      /** Nombre de lignes à afficher (défaut 3). */
      lines?: number;
      className?: string;
    }
  | {
      variant: "rect";
      /** Largeur CSS (ex. `"100%"`, `200`). */
      width?: number | string;
      /** Hauteur CSS (ex. `120`, `"4rem"`). */
      height?: number | string;
      className?: string;
    }
  | {
      variant: "circle";
      /** Diamètre en px. */
      size?: number;
      className?: string;
    };

// Couleur unifiée : champagne-100 (cohérent avec MeteoSkeleton existant).
const BASE = "animate-pulse rounded-md bg-champagne-100";

// Largeurs variées pour la variante `text` (donne un rendu plus naturel
// qu'un bloc rectangulaire monolithique). On cycle sur ce pattern.
const TEXT_LINE_WIDTHS = ["w-full", "w-5/6", "w-2/3"];

export function Skeleton(props: SkeletonProps) {
  const className = props.className ?? "";

  if (props.variant === "text") {
    const lines = props.lines ?? 3;
    return (
      <div className={["space-y-2", className].filter(Boolean).join(" ")} aria-busy="true">
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={[BASE, "h-3", TEXT_LINE_WIDTHS[index % TEXT_LINE_WIDTHS.length]].join(" ")}
          />
        ))}
      </div>
    );
  }

  if (props.variant === "circle") {
    const size = props.size ?? 40;
    return (
      <div
        className={[BASE, "rounded-full", className].filter(Boolean).join(" ")}
        style={{ width: size, height: size }}
        aria-busy="true"
      />
    );
  }

  // variant: "rect"
  const style: CSSProperties = {
    width: props.width ?? "100%",
    height: props.height ?? 16,
  };
  return (
    <div className={[BASE, className].filter(Boolean).join(" ")} style={style} aria-busy="true" />
  );
}
